import {
  DocumentAuditTrailAction,
  DocumentAuditTrailActionLabels,
} from "@/api/cle-api/models/documents";
import CloseIcon from "@/components/icons/CloseIcon";
import DeleteIcon from "@/components/icons/DeleteIcon";
import EditIcon from "@/components/icons/EditIcon";
import FormatLinkIcon from "@/components/icons/FormatLinkIcon";
import IconCheckCircleChecked from "@/components/icons/IconCheckCircleChecked";
import SuccessIcon from "@/components/icons/SuccessIcon";
import SyncIcon from "@/components/icons/SyncIcon";
import UploadIcon from "@/components/icons/UploadIcon";
import UserMoveIcon from "@/components/icons/UserMoveIcon";
import UserRemoveIcon from "@/components/icons/UserRemoveIcon";
import WarningIcon from "@/components/icons/WarningIcon";
import type { ReactNode } from "react";

export type AuditTrailIconVariant =
  | "upload"
  | "approved"
  | "rejected"
  | "deleted"
  | "edited"
  | "linked"
  | "status-changed"
  | "assigned"
  | "resolved"
  | "unassigned"
  | "default";

export interface AuditTrailEventConfig {
  label: string;
  badgeClassName: string;
  iconVariant: AuditTrailIconVariant;
  icon: ReactNode;
}

const iconSize = 18;

const iconProps = { height: iconSize, width: iconSize };

export const getAuditTrailEventConfig = (
  action?: DocumentAuditTrailAction,
): AuditTrailEventConfig => {
  switch (action) {
    case DocumentAuditTrailAction.UPLOADED:
      return {
        label: DocumentAuditTrailActionLabels[action],
        badgeClassName: "bg-light text-dark",
        iconVariant: "upload",
        icon: <UploadIcon {...iconProps} />,
      };
    case DocumentAuditTrailAction.APPROVED:
      return {
        label: DocumentAuditTrailActionLabels[action],
        badgeClassName: "bg-success text-white",
        iconVariant: "approved",
        icon: <IconCheckCircleChecked {...iconProps} />,
      };
    case DocumentAuditTrailAction.REJECTED:
      return {
        label: DocumentAuditTrailActionLabels[action],
        badgeClassName: "bg-danger text-white",
        iconVariant: "rejected",
        icon: <CloseIcon {...iconProps} />,
      };
    case DocumentAuditTrailAction.DELETED:
      return {
        label: DocumentAuditTrailActionLabels[action],
        badgeClassName: "bg-danger text-white",
        iconVariant: "deleted",
        icon: <DeleteIcon {...iconProps} />,
      };
    case DocumentAuditTrailAction.EDITED:
      return {
        label: DocumentAuditTrailActionLabels[action],
        badgeClassName: "bg-light text-dark",
        iconVariant: "edited",
        icon: <EditIcon {...iconProps} />,
      };
    case DocumentAuditTrailAction.LINKED_TO_OBLIGATION:
      return {
        label: DocumentAuditTrailActionLabels[action],
        badgeClassName: "bg-light audit-trail__badge--linked",
        iconVariant: "linked",
        icon: <FormatLinkIcon {...iconProps} />,
      };
    case DocumentAuditTrailAction.STATUS_CHANGED:
      return {
        label: DocumentAuditTrailActionLabels[action],
        badgeClassName: "bg-warning text-dark",
        iconVariant: "status-changed",
        icon: <SyncIcon {...iconProps} />,
      };
    case DocumentAuditTrailAction.ASSIGNED_TO_USER:
      return {
        label: DocumentAuditTrailActionLabels[action],
        badgeClassName: "bg-info text-white",
        iconVariant: "assigned",
        icon: <UserMoveIcon {...iconProps} />,
      };
    case DocumentAuditTrailAction.RESOLVED_BY_USER:
      return {
        label: DocumentAuditTrailActionLabels[action],
        badgeClassName: "bg-success text-white",
        iconVariant: "resolved",
        icon: <SuccessIcon {...iconProps} />,
      };
    case DocumentAuditTrailAction.UNASSIGNED_BY_USER:
      return {
        label: DocumentAuditTrailActionLabels[action],
        badgeClassName: "bg-light text-secondary border",
        iconVariant: "unassigned",
        icon: <UserRemoveIcon {...iconProps} />,
      };
    default:
      return {
        label:
          action && action in DocumentAuditTrailActionLabels
            ? DocumentAuditTrailActionLabels[action as DocumentAuditTrailAction]
            : "Hændelse",
        badgeClassName: "bg-light text-dark",
        iconVariant: "default",
        icon: <WarningIcon {...iconProps} />,
      };
  }
};
